<template lang="html">
  <validation-provider
    :default-values="defaultValues"
    :default-errors="serverErrors"
    :resolver="resolver"
    instant-validate
    @submit="onSubmit"
  >
    <template #default="{ submitted, handleSubmit }">
      <form-layout
        ref="form"
        action="/demo-request"
        method="post"
        :class="$style.form"
        @submit="handleSubmit"
      >
        <fields-layout
          :class="$style.container"
          gap="20px"
        >
          <validation-field
            hidden
            name="_xsrf"
          />
          <validation-field
            hidden
            name="referer"
          />

          <registration-field
            field="company"
            :submitted="submitted"
            :placeholder="i18n('company')"
          />

          <validation-field
            name="type"
            :class="$style.radio"
          >
            <template #default="{ name, onChange, modelValue }">
              <ui-radio-group
                :name="name"
                :model-value="modelValue"
                direction="row"
                :options="typeOptions"
                @update:model-value="onChange($event)"
              />
            </template>
          </validation-field>

          <registration-field
            field="first_name"
            :submitted="submitted"
            :placeholder="i18n('first_name')"
          />
          <registration-field
            field="last_name"
            :submitted="submitted"
            :placeholder="i18n('last_name')"
          />
          <registration-field
            field="email"
            :submitted="submitted"
            :placeholder="i18n('email')"
          />
          <registration-field
            field="phone"
            :submitted="submitted"
            :placeholder="i18n('phone')"
          />
          <registration-field
            field="position"
            :submitted="submitted"
            :placeholder="i18n('position')"
          />

          <validation-field
            name="recruiters_amount"
            :class="$style.selectContainer"
            :hide-errors="!submitted"
          >
            <template #default="{ id, modelValue, onChange, name, invalid }">
              <base-select-dropdown
                :id="id"
                :class="[$style.select, { [$style.invalid]: invalid && submitted }]"
                :items="recruitersSelectOptions"
                :model-value="modelValue"
                show-checkbox
                :placeholder="i18n('recruiters_amount')"
                :name="name"
                @update:model-value="onChange"
              />
            </template>
            <template #error="{ error }">
              <span :class="$style.error">
                {{ error.message }}
              </span>
            </template>
          </validation-field>

          <validation-field
            name="software"
            :class="$style.selectContainer"
            :hide-errors="!submitted"
          >
            <template #default="{ id, modelValue, invalid, onChange, name }">
              <base-select-dropdown
                :id="id"
                :class="[$style.select, { [$style.invalid]: invalid && submitted }]"
                :items="softwareSelectOptions"
                show-checkbox
                :model-value="modelValue"
                :placeholder="i18n('software')"
                :name="name"
                @update:model-value="onChange"
              />
            </template>
            <template #error="{ error }">
              <span :class="$style.error">
                {{ error.message }}
              </span>
            </template>
          </validation-field>

          <base-button
            :class="$style.button"
            type="submit"
          >
            {{ i18n('submit') }}
          </base-button>

          <validation-errors
            name="common"
            :all="false"
          >
            <template #default="{ message }">
              {{ i18n(message) }}
            </template>
          </validation-errors>

          <p
            :class="$style.privacyPolicy"
            v-html="i18n('consent') + privacyPolicyLink"
          />
        </fields-layout>
      </form-layout>
    </template>
  </validation-provider>
</template>

<script setup lang="ts">
import { object, string } from 'yup';
import { useTemplateRef } from 'vue';
import { UiRadioGroup } from '@huntflow/ui';
import RegistrationField from '@/modules/registration/components/RegistrationField.vue';
import BaseButton from '@/shared/legacy/ui/button/button.vue';
import { XSRFHelper } from '@/shared/legacy/lib/util/xsrf-helper';
import FieldsLayout from '@/shared/legacy/ui/fields-layout/fields-layout.vue';
import FormLayout from '@/shared/legacy/ui/form-layout/form-layout.vue';
import {
  ValidationField,
  ValidationProvider,
  yupResolver,
  ValidationErrors
} from '@/shared/legacy/ui/validate-form';
import { useI18N } from '@/shared/legacy/lib/use-i18n';
import BaseSelectDropdown from '@/shared/ui/base-list/base-select-dropdown.vue';

const { fields, serverErrors } = defineProps<{
  fields: any;
  serverErrors: any;
}>();

const i18n = useI18N();

const typeOptions = [
  {
    value: 'internal',
    label: i18n('internal')
  },
  {
    value: 'agency',
    label: i18n('agency')
  }
];

const recruitersSelectOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10 и более'].map((e) => ({
  id: e,
  name: e
}));
const softwareSelectOptions = [
  'Ничего не используем',
  'Excel',
  'E-Staff',
  'Experium',
  'Friendwork',
  'Potok',
  'Talantix',
  'СберПодбор',
  'HRM система (BambooHR, Hurma и др)',
  'CRM (Bitrix24, AmoCRM и др)',
  'Иностранное решение (BreezyHR, Greenhouse и др)',
  'Корп. система (SAP, Axapta, Lotus и др)',
  'Собственная разработка',
  'Другая программа'
].map((e) => ({
  id: e,
  name: e
}));
const defaultValues = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  company: '',
  position: '',
  recruiters_amount: '',
  software: '',
  type: 'internal',
  referer: document.referrer,
  _xsrf: XSRFHelper.getToken(),
  ...fields
};

const resolver = yupResolver(
  object({
    first_name: string().required(),
    last_name: string().required(),
    phone: string().required(),
    email: string().required().email(),
    company: string().required(),
    position: string().required(),
    recruiters_amount: string().required(),
    software: string().required(),
    type: string().required()
  })
);
const privacyPolicyLink = `<a href="/docs/privacy-policy" target="_blank">${i18n('consent.link')}</a>`;

const form = useTemplateRef<HTMLFormElement>('form');

const onSubmit = () => {
  if (form.value) {
    form.value.$el.submit();
  }
};
</script>

<style module>
.form {
  align-items: center;

  h1 {
    font-weight: 500;
    font-size: 45px;
    line-height: 50px;
    text-align: center;
    margin: 0 0 40px;
    color: #16bf54;
  }
  a:hover {
    color: #16bf54;
  }
}

.container {
  max-width: 450px;
  width: 100%;
}

.select {
  height: 46px;
  font-size: 20px;
  transition: all 300ms;
  background: $whiteColor;
  font-family: Graphik, Arial, Tahoma, Verdana, sans-serif;
  outline: none;
  border: 1px solid $grayLight;
  width: 100%;
  border-radius: 3px;
  appearance: none;
  &:focus-within {
    border: 1px solid #16bf54;
    box-shadow: 0 0 10px 0 rgba(22, 191, 84, 0.2);
  }
  @media (max-width: 768px) {
    height: 35px;
    padding: 0 15px;
  }
}

.radio {
  display: flex;
  justify-content: space-around;
  label {
    font-size: 18px;
  }
}

.selectContainer {
  position: relative;
}

.button {
  border-radius: 3px;
  transition: all 300ms;
  white-space: nowrap;
  text-decoration: none;
  width: 100%;
  height: 74px;
  font-size: 22px;
  line-height: 28px;
  color: $whiteColor;
  background-color: #16bf54;
  justify-content: center;

  @media (max-width: 768px) {
    height: 44px;
    font-size: 16px;
    line-height: 22px;
  }

  &_full-size {
    flex-basis: 100%;
  }
  &_center {
    justify-content: center;
  }
}

.invalid {
  border: 1px solid $red-400;
}

.error {
  font-size: 18px;
  color: $red-400;
  line-height: 25px;
  a {
    color: $red-400;
  }
}

.privacyPolicy {
  color: #8f999c;
  font-size: 16px;
  line-height: 22px;
  a {
    color: #8f999c;
  }
}
</style>

<i18n lang="json">
{
  "first_name": {
    "ru_RU": "Ваше имя",
    "en_US": "First name"
  },
  "last_name": {
    "ru_RU": "Ваша Фамилия",
    "en_US": "Last name"
  },
  "company": {
    "ru_RU": "Название компании",
    "en_US": "Company name"
  },
  "phone": {
    "ru_RU": "Телефон",
    "en_US": "Phone number"
  },
  "email": {
    "ru_RU": "Ваша эл. почта",
    "en_US": "Email"
  },
  "email_repeat": {
    "ru_RU": "Повторите вашу эл. почту",
    "en_US": "Email"
  },
  "position": {
    "ru_RU": "Ваша должность",
    "en_US": "Job title"
  },
  "recruiters_amount": {
    "ru_RU": "Число рекрутеров",
    "en_US": "Number of recruiters"
  },
  "agency": {
    "ru_RU": "Кадровое агентство",
    "en_US": "Recruitment Agency"
  },
  "internal": {
    "ru_RU": "Внутренний HR",
    "en_US": "In-House HR"
  },
  "software": {
    "ru_RU": "В чем ведете базу резюме сейчас",
    "en_US": "What you're keeping your resume database in now"
  },
  "submit": {
    "ru_RU": "Заказать презентацию",
    "en_US": "Request a presentation"
  },
  "consent": {
    "ru_RU": "Отправляя форму, я&nbsp;даю согласие на&nbsp;обработку перс. данных в&nbsp;соответствии с&nbsp;",
    "en_US": "By submitting this form, I consent to the processing of my personal data in accordance with Huntflow's"
  },
  "consent.link": {
    "ru_RU": "Политикой&nbspконфиденциальности",
    "en_US": "privacy&nbsppolicy"
  },
  "error.too_freq": {
    "ru_RU": "Слишком много запросов",
    "en_US": "Too many requests"
  }
}
</i18n>
